import { type QueryClient, useQuery } from '@tanstack/react-query'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'
import { appendListPageParams, DEFAULT_LIST_PAGE_SIZE } from '@/lib/list-pagination'
import { useAuth } from './use-auth'

const JOB_PROSPECT_MATCHES_STALE_MS = 10 * 60 * 1000

export type JobProspectMatch = {
  rank: number
  profile_id: string
  name: string
  headline: string
  top_skills: string[]
  semantic_score: number
  structured_score?: number
  final_score?: number
  match_percent: number
  evaluation_id?: string | null
  analysis_headline?: string | null
  was_llm_judged?: boolean
  avatar_url?: string | null
}

export type JobProspectEmptyReason =
  | 'embedding_unavailable'
  | 'opportunity_incomplete'
  | 'no_embedded_profiles'
  | 'all_matched_applied'
  | 'no_strong_matches'

export type JobProspectMatchesResponse = {
  opportunity_id: string
  matches: JobProspectMatch[]
  empty_reason?: JobProspectEmptyReason | null
  page?: number
  page_size?: number
  total?: number
  has_next?: boolean
}

export type JobProspectMatchesError = Error & { status?: number; code?: string }

export type JobProspectMatchesQueryOptions = {
  q?: string
  page?: number
  pageSize?: number
}

export function jobProspectMatchesQueryKey(
  opportunityId: string,
  options: JobProspectMatchesQueryOptions = {},
) {
  return [
    'job-prospects',
    opportunityId,
    options.q?.trim() ?? '',
    options.page ?? 'all',
    options.pageSize ?? DEFAULT_LIST_PAGE_SIZE,
  ] as const
}

export async function fetchJobProspectMatches(
  opportunityId: string,
  options: JobProspectMatchesQueryOptions = {},
): Promise<JobProspectMatchesResponse> {
  const q = options.q?.trim() ?? ''
  const params = new URLSearchParams()
  if (options.page != null) {
    appendListPageParams(params, options.page, options.pageSize ?? DEFAULT_LIST_PAGE_SIZE)
  } else {
    params.set('limit', q ? '50' : '40')
  }
  if (q) params.set('q', q)

  const response = await authorizedFetch(
    `${API_ORIGIN}/api/opportunities/managed/${encodeURIComponent(opportunityId)}/prospect-matches/?${params.toString()}`,
  )
  const data = await response.json().catch(() => ({}))
  if (response.status === 403) {
    const err = new Error('Upgrade required for Suggestions') as JobProspectMatchesError
    err.status = 403
    err.code = typeof data.error === 'string' ? data.error : 'entitlement_denied'
    throw err
  }
  if (!response.ok) {
    const err = new Error('Failed to load prospect matches') as JobProspectMatchesError
    err.status = response.status
    throw err
  }
  return data as JobProspectMatchesResponse
}

export function prefetchJobProspectMatches(
  queryClient: QueryClient,
  opportunityId: string,
  options: JobProspectMatchesQueryOptions = {},
): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: jobProspectMatchesQueryKey(opportunityId, options),
    queryFn: () => fetchJobProspectMatches(opportunityId, options),
    staleTime: JOB_PROSPECT_MATCHES_STALE_MS,
  })
}

export function useJobProspectMatches(
  opportunityId: string | undefined,
  options?: { enabled?: boolean; q?: string; page?: number; pageSize?: number },
) {
  const { token } = useAuth()
  const enabled = (options?.enabled ?? true) && !!token && !!opportunityId
  const q = options?.q?.trim() ?? ''
  const queryOptions: JobProspectMatchesQueryOptions = {
    q,
    page: options?.page,
    pageSize: options?.pageSize,
  }

  return useQuery({
    queryKey: jobProspectMatchesQueryKey(opportunityId ?? '', queryOptions),
    queryFn: () => fetchJobProspectMatches(opportunityId!, queryOptions),
    enabled,
    retry: false,
    staleTime: JOB_PROSPECT_MATCHES_STALE_MS,
  })
}
